import { store as reduxStore } from 'index'
import { setTempEnvParam, getTempEnvParam } from 'env'
import i18n from 'i18n'
import { resolveTimeFormats } from 'utils'

import { setUserEmail } from '../helper/utils'
import { initMailView } from '../helper/mailview'
import { resolveNineNative } from '../helper/nineNative'
import { createSingleUseFunc } from '../helper/singleUse'
import findTextObserver from 'emailThreadViewer/TextFinder/utils/findTextObserver'

// `singleUse` 는 helper/singleUse.ts 가 owner — 여기서 augment 하지 않음.
interface IFaceWindow {
  NineNative?: any
  isReactReady?: boolean
  isMailViewReady?: boolean
  webkit?: {
    messageHandlers?: {
      nineHandler?: {
        postMessage: (message: string) => void
      }
    }
  }
  printObj?: {
    onBefore: () => void
    onAfter: () => void
    mediaQuery: MediaQueryList
    handlePrintMediaQuery: () => void
  }
  docUtil?: {
    removeViewElement: (emailId: string) => void
  }
  parent?: any
}

declare const window: IFaceWindow & Window

interface TimeFormats {
  dateFormat: string
  timeFormat: string
  dateTimeFormat: string
}

interface Settings {
  dateFormat?: string
  timeFormat?: string
  dateTimeFormat?: string
  defaultBgColor?: string
  useAiSummary?: boolean
  [key: string]: any
}

interface ThreadInfo {
  current?: {
    timer1: NodeJS.Timeout | null
    timer2: number | null
    running: boolean
    clear: () => void
  }
}

interface EmailInfo {
  id: string
  messageId: string
  [key: string]: any
}

interface ThreadData {
  info: any
  emailList: EmailInfo[]
}

interface ReactObj {
  addEmail: (
    emailId: string,
    emailInfo: any,
    afterEmailId?: string | null,
    refresh?: boolean,
    isSelected?: boolean | null,
  ) => void
  deleteEmail: (emailId: string) => void
  clearAllEmail: (uiUpdate?: boolean) => void
  forceUpdate: () => void
  select: (emailId: string, foldOthers: boolean, byViewer?: boolean) => void
  setLoading: (loading: boolean) => void
  setInlineImages: (emailId: string, inlineImages: any[]) => void
  notifyEmailChanged: (emailId: string, changedData: any) => void
  notifyThreadInfoChanged: (info: any) => void
  notifyDownloadProgress: (emailId: string, attachmentId: string, progress: number) => void
  setFindText: (findText: string, options?: any) => void
  print: (emailId: string) => void
  checkFoldThread: () => void
  setAiExtractionEmail: (
    aiExtractionEmailId: string,
    update: boolean,
    aiExtractionInitialOpen?: boolean,
  ) => void
  setHideEmptyIcon: (hideEmptyIcon: boolean) => void
  getEmailIds: () => string[]
  extObj?: any
  setOptimizedBtn?: (emailId: string, value: boolean) => void
  setSecureSendInfo?: (
    emailId: string,
    data: { isSecureMail: boolean; expiredDate: string | null; secureMailId: string | null },
  ) => void
  setReadReceiptInfo?: (
    emailId: string,
    isReadReceipt: boolean,
    data: ReadReceiptInfo | null,
  ) => void
}

/**
 * 발송자 본인이 본 자기 수신확인 메일의 status (host → viewer 전달용 페이로드).
 * services/read-receipt의 SendStatusResponse와 동일 모양 — viewer 패키지는
 * services에 의존하지 않도록 자체 타입으로 정의 (JSON 직렬화 기반).
 */
export interface ReadReceiptStatus {
  send_id: string
  sender_email: string
  sender_display_name?: string | null
  subject: string | null
  sent_at: string
  total: number
  read: number
  unread: number
  read_percentage: number
  by_type: {
    to?: { total: number; read: number }
    cc?: { total: number; read: number }
    bcc?: { total: number; read: number }
  }
  recipients: Array<{
    receipt_id: string
    message_id: string
    sender_email: string
    recipient_email: string
    recipient_type: 'to' | 'cc' | 'bcc'
    recipient_display_name?: string | null
    sender_display_name?: string | null
    subject: string | null
    mail_format: string
    pixel_inserted: boolean
    sent_at: string
    status: 'delivered' | 'read'
    is_proxy_read: boolean
    first_read_at: string | null
    first_read_real_at: string | null
    last_read_at: string | null
    read_count: number
    read_count_real: number
    read_count_proxy: number
  }>
}

export interface ReadReceiptInfo {
  receiptId: string
  status: ReadReceiptStatus
}

const timeFormats: TimeFormats = {
  dateFormat: 'L',
  timeFormat: 'LT',
  dateTimeFormat: 'LLLL',
}

const IFace = {
  // helper/singleUse.ts 가 owner — iframe / non-iframe 양쪽 컨텍스트를 일관되게 처리.
  // 1회용 콜백을 store 에 등록하고 native 가 호출할 식별자를 반환.
  createSingleUseFunc: (cb: (param: any) => void): string => {
    return createSingleUseFunc(cb as (param: unknown) => void)
  },
  isPromise: function (value: unknown): value is Promise<unknown> {
    return value != null && typeof (value as { then?: unknown }).then === 'function'
  },
  nativeCall: function (
    funcName: string,
    returnCallback: ((ret: any) => void) | null,
    paramArray?: any[],
  ): boolean {
    const nine = resolveNineNative()
    if (nine != null) {
      try {
        const fn = nine[funcName]
        if (fn != null) {
          const ret = fn.apply(nine, paramArray ?? [])
          if (returnCallback != null) {
            if (IFace.isPromise(ret)) {
              ret
                .then(function (result: unknown) {
                  returnCallback(result)
                })
                .catch(function (e: unknown) {
                  console.error('nativeCall promise error:', funcName, e)
                })
            } else {
              returnCallback(ret)
            }
          }
          return true
        }
      } catch (e) {
        console.log(e)
      }
    } else if (window.webkit?.messageHandlers?.nineHandler?.postMessage) {
      let returnFuncName = ''
      if (returnCallback != null) {
        returnFuncName = IFace.createSingleUseFunc(returnCallback)
      }
      const param: any = {
        function: funcName,
        returnFunction: returnFuncName,
      }
      if (paramArray != null && paramArray.length > 0) {
        param.params = paramArray
      }
      const paramStr = JSON.stringify(param)
      window.webkit.messageHandlers.nineHandler.postMessage(paramStr)
      return true
    }
    return false
  },
  getTimeFormats: (): TimeFormats => {
    return timeFormats
  },
  getSettings: (): Settings => {
    const curSettings = getTempEnvParam('settings')
    return {
      ...curSettings,
    }
  },
  // 동적 property 할당으로 채워지는 placeholder. initIFace() 안에서 host (viewerExt)
  // 와 viewer 간 bridge 객체 (WebToNative / NativeToWeb) 를 받아 IFace 에 할당. 사용처가
  // `IFace.WebToNative.xxx(...)` 와 `IFace.NativeToWeb.TextFinder.setFindText(...)` 등
  // 함수 + nested 객체 혼재 — 단일 type 으로 좁히면 런타임 호환성을 깬다 (architect
  // 검토 MEDIUM 1-4). 본 트랙에서는 `any` 유지하고 영역별 명세 분리는 별도 트랙.
  WebToNative: {} as any,
  NativeToWeb: {} as any,
}

function onReadyIFace(): void {
  if (window.isReactReady === true && window.isMailViewReady === true) {
    IFace.nativeCall('onReady', function (ret) {
      console.log(' -- IFace - onReady - ret : ', ret)
    })
  }
}

// host (apps/owma) 가 iframe.contentWindow 로 받은 viewer window. NativeToWeb 객체를
// 이 window 의 `webObj` property 로 노출해 host 가 호출. NativeToWeb 의 정확한 shape
// 정의는 별도 작업 — 현재는 Record<string, unknown> 으로 가볍게 augment.
interface ViewerWindow extends Window {
  webObj?: Record<string, unknown>
}

export function initIFace(viewerWindow: ViewerWindow, reactObj: ReactObj): () => void {
  console.log('initIFace')

  const funcCalledInfo: Record<string, Record<string, number>> = {}
  const funcCalled = (name: string, emailId: string): void => {
    if (funcCalledInfo[name] == null) {
      funcCalledInfo[name] = {}
    }
    if (funcCalledInfo[name][emailId] == null) {
      funcCalledInfo[name][emailId] = 0
    }
    funcCalledInfo[name][emailId] += 1
  }

  const funcReturned = (name: string, emailId: string): void => {
    funcCalledInfo[name][emailId] -= 1
  }

  const getFuncCalledCnt = (name: string, emailId: string): number => {
    return funcCalledInfo[name][emailId]
  }

  const nativeCallWithAsyncCB = (
    funcName: string,
    paramArray: any[],
    useLoadingCnt: boolean,
  ): Promise<any> => {
    const promise = new Promise((resolve) => {
      const returnCallback = (jsonStr: string | null) => {
        // jsonStr === 'null' 인 경우는 null로 callback에 전달됨.
        // jsonStr == null 인 경우는 데이터 전달을 대기하는 상태임.
        if (jsonStr != null) {
          const jsonData = JSON.parse(jsonStr)
          if (useLoadingCnt === true) {
            funcReturned(funcName, paramArray[0])
            resolve({
              data: jsonData,
              remainLoadingCnt: getFuncCalledCnt(funcName, paramArray[0]),
            })
          } else {
            resolve(jsonData)
          }
        }
      }

      const returnFuncName = IFace.createSingleUseFunc(returnCallback)

      if (useLoadingCnt === true) {
        funcCalled(funcName, paramArray[0])
      }
      const params = [...paramArray, returnFuncName]
      IFace.nativeCall(funcName, returnCallback, params)
    })
    return promise
  }

  const WebToNative = {
    checkAllowedAndKnownSender: (emailId: string, senderEmail: string): Promise<any> => {
      console.log(' --- checkAllowedAndKnownSender : ', emailId, senderEmail)
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'checkAllowedAndKnownSender',
          (retStr: string) => {
            const ret = JSON.parse(retStr)
            resolve(ret)
          },
          [emailId, senderEmail],
        )
      })
      return promise
    },
    addAllowedSender: (sender: string): Promise<any> => {
      console.log(' --- addAllowedSender : ', sender)
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'addAllowedSender',
          (ret: any) => {
            resolve(ret)
          },
          [sender],
        )
      })
      return promise
    },
    addTempAllowedEmailId: (emailId: string): Promise<any> => {
      // 한번 허용한 email을 browser refresh전까지 허용할 수 있도록 설정.
      console.log(' --- addTempAllowedEmailId : ', emailId)
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'addTempAllowedEmailId',
          (ret: any) => {
            resolve(ret)
          },
          [emailId],
        )
      })
      return promise
    },
    getFileIcon: (filename: string): Promise<any> => {
      return nativeCallWithAsyncCB('getFileIcon', [filename], false)
    },
    getAvatar: (email: string, name: string): Promise<any> => {
      return nativeCallWithAsyncCB('getAvatar', [email, name], false)
    },
    getBody: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('getBody', [emailId], false)
    },
    notifySelect: (emailId: string): void => {
      IFace.nativeCall('notifySelect', null, [emailId])
    },
    setRead: (emailId: string, value: boolean, byUser: boolean): Promise<any> => {
      return new Promise((resolve) =>
        IFace.nativeCall('setRead', (ret: any) => resolve(ret), [emailId, value, byUser]),
      )
    },
    delete: (emailId: string): Promise<any> => {
      return new Promise((resolve) =>
        IFace.nativeCall('delete', (ret: any) => resolve(ret), [emailId]),
      )
    },
    newMail: (emailId: string): Promise<any> => {
      return new Promise((resolve) =>
        IFace.nativeCall('newMail', (ret: any) => resolve(ret), [emailId]),
      )
    },
    sendAgain: (emailId: string): Promise<any> => {
      return new Promise((resolve) =>
        IFace.nativeCall('sendAgain', (ret: any) => resolve(ret), [emailId]),
      )
    },
    setFlagged: (emailId: string, value: boolean): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'setFlagged',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, value],
        )
      })
      return promise
    },
    editDraft: (emailId: string): void => {
      IFace.nativeCall('editDraft', null, [emailId])
    },
    deleteDraft: (emailId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'deleteDraft',
          (ret: any) => {
            if (ret === true) {
              reactObj.deleteEmail(emailId)
            }
            resolve(ret)
          },
          [emailId],
        )
      })
      return promise
    },
    openURL: (emailId: string, url: string): void => {
      IFace.nativeCall('openURL', null, [emailId, url])
    },
    showContact: (email: string, name: string): void => {
      IFace.nativeCall('showContact', null, [email, name])
    },
    showFindDialog: (emailId: string): void => {
      IFace.nativeCall('showFindDialog', null, [emailId])
    },
    reply: (emailId: string): void => {
      IFace.nativeCall('reply', null, [emailId])
    },
    replyAll: (emailId: string): void => {
      IFace.nativeCall('replyAll', null, [emailId])
    },
    forward: (emailId: string): void => {
      IFace.nativeCall('forward', null, [emailId])
    },
    forwardAsAttachment: (emailId: string): void => {
      IFace.nativeCall('forwardAsAttachment', null, [emailId])
    },
    getAiSummary: (emailId: string, refresh: boolean): Promise<any> => {
      return nativeCallWithAsyncCB('getAiSummary', [emailId, refresh], true)
    },
    aiSummaryFeedback: (emailId: string, value: string): void => {
      IFace.nativeCall('aiSummaryFeedback', null, [emailId, value])
    },
    getAiExtraction: (emailId: string, refresh: boolean): Promise<any> => {
      return nativeCallWithAsyncCB('getAiExtraction', [emailId, refresh], true)
    },
    aiExtractionFeedback: (emailId: string, value: string): void => {
      IFace.nativeCall('aiExtractionFeedback', null, [emailId, value])
    },
    changeTaskStatus: (emailId: string, taskId: string, taskStatus: string): Promise<any> => {
      return nativeCallWithAsyncCB('changeTaskStatus', [emailId, taskId, taskStatus], false)
    },
    viewMessageDetails: (emailId: string): void => {
      IFace.nativeCall('viewMessageDetails', null, [emailId])
    },
    responseMeeting: (emailId: string, type: string, meetingResponseAction: string): void => {
      IFace.nativeCall('responseMeeting', null, [emailId, type, meetingResponseAction])
    },
    saveEmail: (emailId: string): void => {
      IFace.nativeCall('saveEmail', null, [emailId])
    },
    setPriorityInbox: (emailId: string, value: boolean): void => {
      IFace.nativeCall('setPriorityInbox', null, [emailId, value])
    },
    setThreadImportant: (important: boolean): Promise<any> => {
      return nativeCallWithAsyncCB('setThreadImportant', [important], false)
    },
    onThreadLabelClick: (id: string, name: string): void => {
      IFace.nativeCall('onThreadLabelClick', null, [id, name])
    },
    showInlineImage: (url: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'showInlineImage',
          (ret: any) => {
            resolve(ret)
          },
          [url],
        )
      })
      return promise
    },
    download: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'download',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    cancelDownload: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'cancelDownload',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    downloadAll: (emailId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'downloadAll',
          (ret: any) => {
            resolve(ret)
          },
          [emailId],
        )
      })
      return promise
    },
    showPreview: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'showPreview',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    open: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'open',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    saveAs: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'saveAs',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    saveAndOpen: (emailId: string, attachmentId: string): Promise<any> =>
      new Promise((resolve) => {
        IFace.nativeCall('saveAndOpen', (ret: any) => resolve(ret), [emailId, attachmentId])
      }),
    showInFolder: (emailId: string, attachmentId: string): Promise<any> =>
      new Promise((resolve) => {
        IFace.nativeCall('showInFolder', (ret: any) => resolve(ret), [emailId, attachmentId])
      }),

    changeEventStatus: (emailId: string, eventId: string, status: string): Promise<any> => {
      return nativeCallWithAsyncCB('changeEventStatus', [emailId, eventId, status], false)
    },
    addToEvent: (emailId: string, eventId: string): boolean => {
      return IFace.nativeCall('addToEvent', null, [emailId, eventId])
    },
    clearTaskStatus: (emailId: string, taskId: string): Promise<any> => {
      return nativeCallWithAsyncCB('clearTaskStatus', [emailId, taskId], false)
    },

    reservationMailReschedule: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('reservationMailReschedule', [emailId], false)
    },
    reservationMailCancelSend: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('reservationMailCancelSend', [emailId], false)
    },
    reservationMailDelete: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('reservationMailDelete', [emailId], false)
    },
    secureSendRenewAccess: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('secureSendRenewAccess', [emailId], false)
    },
    secureSendRemoveAccess: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('secureSendRemoveAccess', [emailId], false)
    },
    requestRefreshReadReceiptInfo: (emailId: string): void => {
      // 호스트(apps/owma) 측에서 캐시 삭제 후 applyReadReceiptInfo 재호출.
      // 응답이 setReadReceiptInfo로 다시 들어오므로 nativeCall fire-and-forget — 의도된 설계
      // (tasks.md §Phase 2.7 / openspec design.md L1149-1156). 응답 채널이 비동기 push이므로
      // 콜백 wait이 불필요. (※ 빈 콜백 자체는 review에서 재지적되지 않도록 명기)
      IFace.nativeCall('requestRefreshReadReceiptInfo', () => {}, [emailId])
    },
    openReadReceiptDetailPopup: (emailId: string): void => {
      // 5차 보완: viewer iframe 내부에서 confirmBase 를 호출하면 마스크가 iframe 영역으로
      // 클립되므로, host 컨텍스트(apps/owma)에서 popup 을 마운트한다.
      // host 가 emailId 로 emailInfoMap.readReceiptInfo.status 를 lookup 후 popup 을 띄운다.
      // fire-and-forget 패턴은 requestRefreshReadReceiptInfo 미러 (tasks.md §Phase 5).
      IFace.nativeCall('openReadReceiptDetailPopup', () => {}, [emailId])
    },
  }

  IFace.WebToNative = WebToNative

  const checkTimeFormat = (settings: Settings): void => {
    const resolved = resolveTimeFormats(settings)
    timeFormats.dateFormat = resolved.dateFormat
    timeFormats.timeFormat = resolved.timeFormat
    timeFormats.dateTimeFormat = resolved.dateTimeFormat
  }

  const checkLanguage = (): void => {
    const curSettings = getTempEnvParam('settings')
    if (curSettings != null) {
      checkTimeFormat(curSettings)
    }
  }

  i18n.on('languageChanged', checkLanguage)

  const threadInfo: ThreadInfo = {}

  const NativeToWeb = {
    // addEML: (emailId, emlData) => {
    //   reactObj.addEmail(emailId, emlData, 'eml')
    // },
    setUserEmail: (email: string): void => {
      setUserEmail(email)
    },
    setSettings: (settingsStr: string, refresh: boolean): void => {
      const settings = JSON.parse(settingsStr)
      const oldSettings = getTempEnvParam('settings') || {}
      const newSettings = {
        ...oldSettings,
        ...settings,
      }

      checkTimeFormat(newSettings)

      setTempEnvParam('settings', newSettings)

      if (refresh === true) {
        reactObj.forceUpdate()
      }
    },
    setLanguage: (locale: string): void => {
      console.log(' --- setLanguage : ', locale)
      reduxStore.dispatch({
        type: 'settings/SET_LOCALE',
        payload: {
          locale,
        },
      })
      reactObj.forceUpdate()
    },
    select: (emailId: string, foldOthers: boolean, byViewer?: boolean): void => {
      reactObj.select(emailId, foldOthers, byViewer)
    },
    setOptimizedBtn: (emailId: string, value: boolean): void => {
      reactObj.setOptimizedBtn(emailId, value)
    },
    setAiExtractionEmail: (aiExtractionEmailId: string): void => {
      console.log(' --- setAiExtractionEmail : ', aiExtractionEmailId)
      reactObj.setAiExtractionEmail(aiExtractionEmailId, true)
    },
    getEmailIds: (): string[] => {
      return reactObj.getEmailIds()
    },
    clearThread: (): void => {
      if (threadInfo.current != null) {
        threadInfo.current.clear()
      }

      reactObj.setLoading(true)

      reactObj.clearAllEmail()

      requestAnimationFrame(() => {
        reactObj.setLoading(false)
      })
    },
    setThread: (
      threadDataJsonStr: string,
      defaultEmailId: string | null,
      aiExtractionEmailId: string,
      aiExtractionInitialOpen: boolean,
    ): void => {
      if (threadInfo.current != null) {
        threadInfo.current.clear()
      }

      const currentInfo = {
        timer1: null as NodeJS.Timeout | null,
        timer2: null as number | null,
        running: true,
        clear: (): void => {
          currentInfo.running = false
          if (currentInfo.timer1 != null) {
            clearTimeout(currentInfo.timer1)
          }
          if (currentInfo.timer2 != null) {
            cancelAnimationFrame(currentInfo.timer2)
          }
          requestAnimationFrame(() => {
            reactObj.setLoading(false)
          })
        },
      }
      threadInfo.current = currentInfo

      let threadData: ThreadData | EmailInfo[] = JSON.parse(threadDataJsonStr)

      if (Array.isArray(threadData) === true) {
        threadData = {
          info: null,
          emailList: threadData as EmailInfo[],
        }
      }

      const { info, emailList } = threadData as ThreadData

      const funcList = [
        (): void => {
          if (emailList.length === 0) {
            reactObj.setLoading(true)
            reactObj.clearAllEmail(true)
          }
        },
        (): void => {
          // console.log(' --- thread info : ', info, emailList);
          if (emailList.length > 0) {
            reactObj.clearAllEmail(false)
          }

          emailList.forEach((emailInfo: EmailInfo, idx: number) => {
            if (currentInfo.running === true) {
              const isSelected = defaultEmailId == null ? null : emailInfo.id === defaultEmailId
              reactObj.addEmail(emailInfo.id, emailInfo, null, false, isSelected)

              if ((idx + 1) % 5 === 0) {
                setTimeout(() => {
                  reactObj.forceUpdate()
                }, 20)
              }
            }
          })

          reactObj.checkFoldThread()

          reactObj.notifyThreadInfoChanged(info)

          reactObj.setAiExtractionEmail(aiExtractionEmailId, false, aiExtractionInitialOpen)

          reactObj.forceUpdate()
        },
        (): void => {
          if (defaultEmailId != null) {
            reactObj.select(defaultEmailId, true)
          } else {
            reactObj.forceUpdate()
          }
        },
        (): void => {
          reactObj.setLoading(false)
        },
      ]

      const iter = funcList[Symbol.iterator]()

      const callFunc = (): void => {
        const obj = iter.next()
        if (obj.done === false) {
          obj.value()
          currentInfo.timer1 = setTimeout(() => {
            currentInfo.timer1 = null
            currentInfo.timer2 = requestAnimationFrame(() => {
              currentInfo.timer2 = null
              if (currentInfo.running === true) {
                callFunc()
              }
            })
          }, 30)
        }
      }

      callFunc()
    },
    setHideEmptyIcon: (hideEmptyIcon: boolean): void => {
      reactObj.setHideEmptyIcon(hideEmptyIcon)
    },
    onSetInlineImages: (emailId: string, inlineImagesStr: string): void => {
      const inlineImages = JSON.parse(inlineImagesStr)
      const inlineImagesArr = Array.isArray(inlineImages) === true ? inlineImages : [inlineImages]

      reactObj.setInlineImages(emailId, inlineImagesArr)
    },
    setSecureSendInfo: (emailId: string, dataJsonStr: string): void => {
      const data = JSON.parse(dataJsonStr)
      if (reactObj.setSecureSendInfo != null) {
        reactObj.setSecureSendInfo(emailId, data)
      }
    },
    setReadReceiptInfo: (
      emailId: string,
      isReadReceipt: boolean,
      dataJsonStr: string | null,
    ): void => {
      const data =
        dataJsonStr != null && dataJsonStr.length > 0
          ? (JSON.parse(dataJsonStr) as ReadReceiptInfo)
          : null
      if (reactObj.setReadReceiptInfo != null) {
        reactObj.setReadReceiptInfo(emailId, isReadReceipt, data)
      }
    },
    addEmail: (emailInfoStr: string): void => {
      const emailInfo = JSON.parse(emailInfoStr)
      reactObj.addEmail(emailInfo.id, emailInfo)
    },
    insertEmail: (emailInfoStr: string, afterEmailId: string): void => {
      const emailInfo = JSON.parse(emailInfoStr)
      reactObj.addEmail(emailInfo.id, emailInfo, afterEmailId)
    },
    deleteEmail: (emailId: string): void => {
      reactObj.deleteEmail(emailId)
    },
    notifyEmailChanged: (emailId: string, changedDataStr: string): void => {
      const changedData = JSON.parse(changedDataStr)
      reactObj.notifyEmailChanged(emailId, changedData)
    },
    notifyThreadInfoChanged: (infoJsonStr: string): void => {
      const changedData = JSON.parse(infoJsonStr)
      reactObj.notifyThreadInfoChanged(changedData)
    },
    notifyDownloadProgress: (emailId: string, attachmentId: string, progress: number): void => {
      reactObj.notifyDownloadProgress(emailId, attachmentId, progress)
    },

    /** @deprecated TextFinder.setFindText 사용*/
    setFindText: (findText: string, options?: string): void => {
      reactObj.setFindText(findText, options && JSON.parse(options))
    },

    TextFinder: {
      setFindText: (findText: string, options?: string): void =>
        findTextObserver.notify(findText, options && JSON.parse(options)),
      // TODO
      closeSearchBar: (): null => null,
    },
    print: (emailId: string): void => {
      reactObj.print(emailId)
    },
  }

  IFace.NativeToWeb = NativeToWeb

  // jquery / linkify dynamic inject 블록은 §6.3 (linkify-modernization) 으로 제거.
  // helper/linkifyMail 가 모듈 import 로 viewer 번들에 포함되며, EmailView /
  // AiExtraction/Events 가 마운트 시점에서 linkifyMailNode 를 직접 호출.

  initMailView()
  viewerWindow.webObj = IFace.NativeToWeb
  reactObj.extObj = IFace.WebToNative

  window.isReactReady = true
  onReadyIFace()

  // InnerViewer 모드(viewerWindow === host window)에서는 iframe 폐기가 없어, host window 의
  // webObj 가 NativeToWeb 클로저 → reactObj(EmailContainer) 를 영구 보존해 누수가 된다.
  // (iframe 모드는 about:blank 로 contentWindow 가 폐기되며 함께 사라짐.)
  // EmailContainer.componentWillUnmount 에서 호출 — 이 인스턴스가 등록한 bridge 일 때만
  // 정확히 해제한다(다중 마운트 경쟁 시 새 인스턴스 등록을 지우지 않도록 토큰 식별).
  return () => {
    if (viewerWindow.webObj === NativeToWeb) {
      delete viewerWindow.webObj
    }
    if (IFace.NativeToWeb === NativeToWeb) {
      IFace.NativeToWeb = {}
    }
    if (IFace.WebToNative === WebToNative) {
      IFace.WebToNative = {}
    }
    window.isReactReady = false
  }
}

export default IFace
